import { func, object, string } from 'prop-types'

import { useEffect, useState } from 'react'
import { APIS } from '../../../config/apis'
import { ServerRequest } from '../../../utils/apiweb'
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { onRequestError, onRequestNoSuccess } from '../../../utils/requests'
import {TIPS} from "../../../consts/tips";

import { FormError, FormLabel, InputEntidad, Modal } from "../../common"
import { useForm, useFormErrors, useManagedContext } from "../../hooks"
import { isNull, isValidNumber } from '../../../utils/validator'
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage'

const entityInit = {
    id: -1,
    codigo: '',
    nombre: '',
    orden: '',
    limiteInferior: 0,
    limiteSuperior: 0,
    codigoSucerp: '',   
}

const CategoriaVehiculoModal = ({ editMode, editItem, onConfirm, onCancel }) => {
    const { setIsLoading } = useManagedContext()

    const [entity, setEntity] = useState(entityInit)
    const [formValues, formHandle, _, formSet] = useForm({
        codigo: '',
        nombre: '',
        orden: '',
        limiteInferior: 0,
        limiteSuperior: 0,
        codigoSucerp: '',
        idIncisoVehiculo: 0,
    })

    useEffect(() => {
        setEntity(editItem)
        formSet({
            codigo: editItem.codigo,
            nombre: editItem.nombre,
            orden: editItem.orden,
            limiteInferior: editItem.limiteInferior,
            limiteSuperior: editItem.limiteSuperior,
            codigoSucerp: editItem.codigoSucerp,
            idIncisoVehiculo: editItem.idIncisoVehiculo
        })
    }, [editItem])

    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors)

    function getErrors() {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED_SHORT
        if (formValues.nombre.length < 1) errors.nombre = VALIDATION_MESSAGE.REQUIRED
        if (isEmptyValue(formValues.orden)) errors.orden = VALIDATION_MESSAGE.REQUIRED
        if (!isValidNumber(formValues.limiteInferior, false)) errors.limiteInferior = VALIDATION_MESSAGE.REQUIRED
        if (isEmptyValue(formValues.limiteSuperior)) errors.limiteSuperior = VALIDATION_MESSAGE.REQUIRED
        else if (!isValidNumber(formValues.limiteSuperior, true)) errors.limiteSuperior = 'Ingresá un número mayor a cero'
        if (!isValidNumber(formValues.idIncisoVehiculo, true)) errors.idIncisoVehiculo = VALIDATION_MESSAGE.REQUIRED_OPTION

        return errors
    }

    function getFormatErrors() {
        const errors = {}
        if (editMode === 'view') return errors

        if (!isEmptyValue(formValues.orden) && !isValidNumber(formValues.orden, true)) errors.orden = 'Ingresá un número mayor a cero'

        return errors
    }

    function isEmptyValue(valor) {
        return isNull(valor) || valor.toString().length === 0
    }

    const onClickConfirm = () => {
        if (!validate()) return

        setIsLoading(true)

        ServerRequest(
            editMode === 'add' ? REQUEST_METHOD.POST : REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CATEGORIA_VEHICULO,
            editMode === 'modify' ? `/${entity.id}` : '',
            {
                ...entity,
                ...formValues,
                orden: parseInt(formValues.orden) ?? 0,
                limiteInferior: parseInt(formValues.limiteInferior) ?? 0,
                limiteSuperior: parseInt(formValues.limiteSuperior) ?? 0,
            },
            res => res.json().then(() => {
                setIsLoading(false)
                onConfirm()

                const message = editMode === 'add' ? "La categoría fue generada correctamente" : "La categoría fue actualizada correctamente"
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, message)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            error => {
                onRequestError(error)
                setIsLoading(false)
            },
        )
    }

    const disabled = editMode === 'view'

    return (
        <Modal
            onEscape={onCancel}
            size="lg"
            title="Categoría de vehículo"
            tipName={TIPS.NAME.MODALS.ENTIDAD_MODAL}
            body={(
                <div className="row form-basic">
                    <div className="col-12 col-md-2">
                        <FormLabel htmlFor="codigo" required>Código</FormLabel>
                        <input
                            type="text"
                            name="codigo"
                            maxLength={50}
                            className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                            value={formValues.codigo}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('codigo')} />
                    </div>
                    <div className="col-12 col-md-4">
                        <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                        <input
                            type="text"
                            name="nombre"
                            className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                            value={formValues.nombre}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('nombre')} />
                    </div>
                    <div className="col-12 col-md-3">
                        <FormLabel htmlFor="orden" required>Orden</FormLabel>
                        <input
                          name="orden"
						  type="number"
						  placeholder=""
						  className={`form-control ${errorOf('orden') ? 'is-invalid' : ''}`}
						  value={formValues.orden}
						  onChange={formHandle}
						  disabled={disabled}
                        />
                        <FormError message={errorOf('orden')} />
                    </div>
                    <div className="col-12 col-md-3">
                        <label htmlFor="codigoSucerp" className="form-label">Código Suats</label>
                        <input
                            type="text"
                            name="codigoSucerp"
                            maxLength={50}
                            className="form-control"
                            value={formValues.codigoSucerp}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError />
                    </div>
                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="idIncisoVehiculo" required>Inciso vehículo</FormLabel>
                        <InputEntidad
                            name="idIncisoVehiculo"
                            placeholder=""
                            className={`form-control ${errorOf('idIncisoVehiculo') ? 'is-invalid' : ''}`}
                            value={formValues.idIncisoVehiculo}
                            onChange={formHandle}
                            title="Inciso vehículo"
                            entidad="IncisoVehiculo"
                            onFormat= {(row) => (row && row.id) ? `${row.codigo} - ${row.nombre}` : ''}
                        />
                        <FormError message={errorOf('idIncisoVehiculo')} />
                    </div>
                    <div className="col-12 col-md-3">
                        <FormLabel htmlFor="limiteInferior" required>Límite inferior</FormLabel>
                        <input
                          name="limiteInferior"
						  type="number"
						  placeholder=""
						  className={`form-control ${errorOf('limiteInferior') ? 'is-invalid' : ''}`}
						  value={formValues.limiteInferior}
						  onChange={formHandle}
						  disabled={disabled}
                        />
                        <FormError message={errorOf('limiteInferior')} />
                    </div>
                    <div className="col-12 col-md-3">
                        <FormLabel htmlFor="limiteSuperior" required>Límite superior</FormLabel>
                        <input
                          name="limiteSuperior"
						  type="number"
						  placeholder=""
						  className={`form-control ${errorOf('limiteSuperior') ? 'is-invalid' : ''}`}
						  value={formValues.limiteSuperior}
						  onChange={formHandle}
						  disabled={disabled}
                        />
                        <FormError message={errorOf('limiteSuperior')} />
                    </div>
                </div>
            )}
            footer={(
                <div className="width-100p disp-b">
                    <button className="btn back-button m-left-5" data-dismiss="modal" onClick={onCancel}>Volver</button>
                    {editMode !== 'view' && (
                        <button className="btn action-button" data-dismiss="modal" onClick={() => onClickConfirm()}>Guardar</button>
                    )}
                </div> 
            )}
        />
    )
}

CategoriaVehiculoModal.propTypes = {
    editItem: object,
    editMode: string,
    onConfirm: func.isRequired,
    onCancel: func.isRequired,
}

export default CategoriaVehiculoModal
